import {useContext, useEffect, useState} from 'react';
import {FlagContext} from '@unleash/proxy-client-react';

const hasSettled = client =>
  Boolean(client?.fetchedFromServer || client?.getError?.());

/**
 * Whether the Unleash client has resolved real flag values this session —
 * the first toggle fetch either landed ('update') or failed ('error').
 *
 * The provider's flagsReady/READY signals can't distinguish this: the app
 * boots the client with bootstrap toggles (src/constants/unleash), which emit
 * READY before any fetch, so flags read as off while the truth is still in
 * flight.
 */
const useUnleashFlagsSettled = () => {
  // Read the context directly — useUnleashClient throws without a provider,
  // and environments without one (e.g. some test setups) should resolve
  // instead of loading forever.
  const client = useContext(FlagContext)?.client;
  const [isSettled, setIsSettled] = useState(
    () => !client || hasSettled(client),
  );

  useEffect(() => {
    if (!client || isSettled) return undefined;

    // The first fetch may have landed between render and this effect.
    if (hasSettled(client)) {
      setIsSettled(true);

      return undefined;
    }

    const settle = () => setIsSettled(true);
    // Metrics errors also emit 'error' but say nothing about the toggles.
    const settleOnError = error => {
      if (error?.type !== 'metrics') settle();
    };

    client.on('update', settle);
    client.on('error', settleOnError);

    return () => {
      client.off('update', settle);
      client.off('error', settleOnError);
    };
  }, [client, isSettled]);

  return isSettled;
};

export default useUnleashFlagsSettled;
